import { CurrencyType, getCurrencyConfig, getPlatformFeeRate } from '@okletsplay/core';
import { calculatePlatformFeeFromPrimaryAmount, convertReverseCrypto } from './currency';
import {
  calculateBuyAmountToSwapToCoverPlatformFeeCurrency,
  QUICK_SWAP_BUFFER_PERCENT,
} from './swap-utils';
import { ExchangeRateDto, UserBalanceDto } from 'app/interfaces/dto';
import { selectExchangeRate } from './exchange-rate';
import { sum } from './calculations';

interface AmountToBuyArguments {
  mainAmount: number;
  userAccounts: (UserBalanceDto | undefined)[];
  exchangeRates: ExchangeRateDto[];
  gameActivityFeePercentage: number;
  currencyType: CurrencyType;
}

interface CalculateOptions {
  mainAmount: number;
  mainCurrencyBalance: number;
  exchangeRate: number;
  gameActivityFeePercentage: number;
}

function calculateSuggestedAmountMultiCurrency(
  options: CalculateOptions & { platformFeeCurrencyBalance: number }
): number {
  const {
    mainAmount: mainCurrencyAmount,
    platformFeeCurrencyBalance,
    mainCurrencyBalance,
    exchangeRate,
    gameActivityFeePercentage,
  } = options;
  if (mainCurrencyAmount < mainCurrencyBalance) {
    return 0;
  }

  const mainCurrencyDiff = mainCurrencyAmount - mainCurrencyBalance;

  const platformFeeCurrencyAmount = calculatePlatformFeeFromPrimaryAmount(
    mainCurrencyAmount,
    exchangeRate,
    gameActivityFeePercentage
  );

  if (platformFeeCurrencyAmount <= platformFeeCurrencyBalance) {
    return mainCurrencyDiff;
  }

  const platformFeeInMainCurrency = (mainCurrencyAmount * gameActivityFeePercentage) / 100;
  const newMainCurrencyBalance = mainCurrencyAmount + platformFeeInMainCurrency;
  const newMainCurrencyBalancePlusQuickSwapBuffer =
    newMainCurrencyBalance + newMainCurrencyBalance * QUICK_SWAP_BUFFER_PERCENT;

  const { suggestedSwapBuyAmount } = calculateBuyAmountToSwapToCoverPlatformFeeCurrency({
    ...options,
    mainCurrencyAmount: options.mainAmount,
    mainCurrencyBalance: newMainCurrencyBalancePlusQuickSwapBuffer,
  });
  const suggestedPlatformFeeAmountInMainCurrency = convertReverseCrypto(
    suggestedSwapBuyAmount,
    exchangeRate
  );

  return Math.ceil(mainCurrencyDiff + suggestedPlatformFeeAmountInMainCurrency);
}

function calculateSuggestedAmountSingleCurrency({
  mainAmount,
  exchangeRate,
  gameActivityFeePercentage,
  mainCurrencyBalance,
}: CalculateOptions): number {
  const platformFeeAmount = calculatePlatformFeeFromPrimaryAmount(
    mainAmount,
    exchangeRate,
    gameActivityFeePercentage
  );

  const total = sum(mainAmount, platformFeeAmount);

  if (total <= mainCurrencyBalance) {
    return 0;
  }

  return Math.ceil(total - mainCurrencyBalance);
}

export const calculateSuggestedAmountToBuy = (options: AmountToBuyArguments): number => {
  const currenciesConfig = getCurrencyConfig(options.currencyType);
  const exchangeRateName = getPlatformFeeRate(options.currencyType);

  if (!currenciesConfig || !currenciesConfig.platformFee || !exchangeRateName) {
    return 0;
  }

  const exchangeRate = selectExchangeRate(options.exchangeRates, exchangeRateName);

  if (!exchangeRate) {
    return 0;
  }

  const { mainAmount, gameActivityFeePercentage } = options;

  const mainCurrencyBalance = options.userAccounts.find(
    (account) => account?.currency === currenciesConfig?.main
  )?.balance;

  if (!mainCurrencyBalance) {
    return 0;
  }

  const platformFeeCurrencyBalance =
    options.userAccounts.find((account) => account?.currency === currenciesConfig?.platformFee)
      ?.balance ?? 0;

  switch (options.currencyType) {
    case CurrencyType.OKLP:
      return 0;
    case CurrencyType.USDT_OKLP: {
      return calculateSuggestedAmountMultiCurrency({
        mainCurrencyBalance,
        platformFeeCurrencyBalance,
        mainAmount,
        exchangeRate,
        gameActivityFeePercentage,
      });
    }
    case CurrencyType.USDT_USDT: {
      return calculateSuggestedAmountSingleCurrency({
        mainCurrencyBalance,
        mainAmount,
        exchangeRate,
        gameActivityFeePercentage,
      });
    }
  }
};
